import { API_URL, authenticatedFetch } from './base'

export type NotificationStatus = 'scheduled' | 'delivered' | 'canceled'

export interface AppNotification {
  id: number
  user_id: number
  title: string
  body: string
  data: Record<string, unknown> | null
  source: string
  scheduled_for: string | null
  dedupe_key: string | null
  status: NotificationStatus
  delivered_at: string | null
  read_at: string | null
  created_at: string
}

export interface NotificationCreate {
  title: string
  body: string
  data?: Record<string, unknown>
  source?: string
  scheduled_for?: string
  dedupe_key?: string
}

export async function getNotifications(params?: {
  status?: NotificationStatus
  limit?: number
}): Promise<AppNotification[]> {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  const url = `${API_URL}/notifications${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
  const response = await authenticatedFetch(url)
  if (!response.ok) throw new Error('Failed to fetch notifications')
  return response.json()
}

export async function getUnreadCount(): Promise<number> {
  const response = await authenticatedFetch(`${API_URL}/notifications/unread-count`)
  if (!response.ok) throw new Error('Failed to fetch unread count')
  const body: { count: number } = await response.json()
  return body.count
}

export async function createNotification(
  notification: NotificationCreate
): Promise<AppNotification> {
  const response = await authenticatedFetch(`${API_URL}/notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(notification),
  })
  if (!response.ok) throw new Error('Failed to create notification')
  return response.json()
}

export async function markNotificationRead(id: number): Promise<AppNotification> {
  const response = await authenticatedFetch(`${API_URL}/notifications/${id}/read`, {
    method: 'POST',
  })
  if (!response.ok) throw new Error('Failed to mark notification read')
  return response.json()
}

export async function markAllNotificationsRead(): Promise<number> {
  const response = await authenticatedFetch(`${API_URL}/notifications/read-all`, {
    method: 'POST',
  })
  if (!response.ok) throw new Error('Failed to mark all notifications read')
  const body: { marked: number } = await response.json()
  return body.marked
}

export async function cancelNotification(id: number): Promise<AppNotification> {
  const response = await authenticatedFetch(`${API_URL}/notifications/${id}`, {
    method: 'DELETE',
  })
  if (!response.ok) throw new Error('Failed to cancel notification')
  return response.json()
}
